import { useEmportees, useSupprimerBulle } from '../partage/useArbre';
import { useAuPouce } from '../partage/surface';
import { useEchap } from '../partage/useEchap';
import { MOTS_ETAT, motsDuDossier } from '../partage/vocabulaire';
import './SupprimerDossier.css';

/**
 * La confirmation avant de supprimer une bulle depuis sa fiche (ticket 10 de
 * retours-testeurs). La même forme que celle d'un dossier (`SupprimerDossier`), dont elle
 * reprend le dessin : voile clair, panneau au centre, « Garder » qui a le focus et la
 * lueur, pas de rouge. Supprimer emporte tout le sous-arbre et ne se rattrape pas.
 *
 * Le nombre est demandé au serveur à l'ouverture : la fiche ne connaît que les enfants
 * directs. Tant qu'il n'est pas là, « Supprimer » attend : on ne confirme pas à l'aveugle.
 */
export function SupprimerBulle({
  bulle,
  onGarder,
  onSupprimee,
  auDessus = false,
}: {
  bulle: { id: string; titre: string; parentId: string | null };
  onGarder: () => void;
  onSupprimee: () => void;
  /** Ouverte depuis l'inventaire (ticket 18) : son voile couvre aussi l'inventaire. */
  auDessus?: boolean;
}) {
  const emportees = useEmportees(bulle.id);
  const supprimer = useSupprimerBulle();
  const m = motsDuDossier(useAuPouce());
  const dossier = bulle.parentId === null;
  const genre = dossier ? 'il' : 'elle';
  const le = dossier ? 'le' : 'la';

  // Échap garde la bulle ; la fiche dessous ne l'entend pas.
  useEchap(true, onGarder, 'garde');

  return (
    <>
      <div
        className={`supprimer-voile${auDessus ? ' supprimer--au-dessus' : ''}`}
        onClick={onGarder}
        aria-hidden="true"
      />

      <div
        className={`supprimer${auDessus ? ' supprimer--au-dessus' : ''}`}
        data-tuto="supprimer"
        role="alertdialog"
        aria-label={`supprimer ${bulle.titre}`}
        data-bulle={bulle.id}
      >
        <h2>Supprimer «&nbsp;{bulle.titre}&nbsp;»&nbsp;?</h2>

        <p className="supprimer-emporte">
          {emportees.data
            ? ceQuiPart(emportees.data.bulles, genre)
            : emportees.isError
              ? `Impossible de compter ce qu’${genre} contient pour l’instant. Rien n’a bougé.`
              : `Je compte ce qu’${genre} contient…`}
        </p>

        <p className="supprimer-impasse">
          Supprimer sert à ce qui ne te concerne pas, aux erreurs et aux doublons. Pour une idée
          que tu ne fais pas maintenant, garde-{le} et mets-{le} «&nbsp;{MOTS_ETAT.sans_objet}&nbsp;»
          : {genre} ne compte pas dans ce qui reste à faire.
        </p>

        {supprimer.isError && (
          <p className="refus supprimer-refus">
            {dossier
              ? m.pasSupprime
              : 'La bulle n’a pas pu être supprimée. Elle est toujours là, rien n’a bougé.'}
          </p>
        )}

        <div className="supprimer-gestes">
          <button
            type="button"
            className="bouton bouton-discret supprimer-confirmer"
            data-tuto="supprimer-confirmer"
            onClick={() => supprimer.mutate(bulle.id, { onSuccess: onSupprimee })}
            disabled={!emportees.data || supprimer.isPending}
          >
            Supprimer
          </button>
          <button type="button" className="bouton" onClick={onGarder} autoFocus>
            {dossier ? m.garder : 'Garder la bulle'}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Le nombre dit en mots, parce que c'est lui qu'on doit lire avant de confirmer. « il »
 * pour un dossier, « elle » pour une bulle ; le reste de la phrase ne change pas.
 */
export function ceQuiPart(bulles: number, genre: 'il' | 'elle'): string {
  const sujet = genre === 'il' ? 'Il' : 'Elle';
  const avec = genre === 'il' ? 'lui' : 'elle';
  if (bulles === 0) return `${sujet} est vide : rien d’autre ne part avec ${avec}.`;
  if (bulles === 1) {
    return `La bulle qu’${genre} contient part avec ${avec}. Rien ne se récupère.`;
  }
  return `Les ${bulles} bulles qu’${genre} contient partent avec ${avec}. Rien ne se récupère.`;
}
